"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";

const SECTION_COPY = [
  {
    href: "/money-flow/funding/composite",
    title: "Leverage Overview",
    subtitle:
      "Combined futures, financed longs, and short-side pressure so you can read positioning in one place.",
  },
  {
    href: "/money-flow/funding/blank-sale",
    title: "Short Pressure",
    subtitle:
      "Blank-sale concentration, crowding, and trend when the main question is where the market is leaning bearish.",
  },
  {
    href: "/money-flow/funding/mts",
    title: "Funded Longs: MTS",
    subtitle:
      "Margin trading financing positions and open exposure when you want the cleaner MTS-specific read.",
  },
  {
    href: "/money-flow/funding/mfs",
    title: "Funded Longs: MFS",
    subtitle:
      "Free-float financing and crowding risk when funded float matters more than the broader leverage stack.",
  },
  {
    href: "/money-flow/funding/margins",
    title: "Risk Limits",
    subtitle:
      "VAR and haircut constraints for understanding where tighter financing rules may affect the tape.",
  },
] as const;

export default function FundingLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const currentSection =
    SECTION_COPY.find((section) => pathname?.startsWith(section.href)) ?? {
      href: "/money-flow/funding/composite",
      title: "Leverage & Short Pressure",
      subtitle:
        "Leverage, funded longs, short pressure, and risk limits in one positioning workspace.",
    };

  return (
    <>
      {pathname !== "/money-flow/funding" && <section className="page-header">
        <div className="page-header-container">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "1rem",
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                display: "grid",
                gap: "0.45rem",
                maxWidth: "760px",
                minWidth: 0,
                flex: "1 1 320px",
              }}
            >
              <span
                style={{
                  fontSize: "0.74rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  color: "var(--blue-text)",
                }}
              >
                Leverage & Short Pressure
              </span>
              <h1
                className="page-title"
                style={{
                  overflowWrap: "anywhere",
                  fontSize: "clamp(1.15rem, 4.6vw, 2.5rem)",
                  lineHeight: 1.12,
                }}
              >
                {currentSection.title}
              </h1>
              <p
                className="page-subtitle"
                style={{
                  maxWidth: "min(320px, 100%)",
                  overflowWrap: "anywhere",
                  fontSize: "clamp(0.9rem, 3.8vw, 1.125rem)",
                }}
              >
                {currentSection.subtitle}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "0.7rem",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <Link
                href="/money-flow"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "40px",
                  padding: "0.65rem 0.9rem",
                  borderRadius: "999px",
                  border: "1px solid rgba(148, 163, 184, 0.22)",
                  background: "rgba(11, 14, 20, 0.56)",
                  color: "rgba(241, 245, 249, 0.86)",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                }}
              >
                Back to Money Flow
              </Link>
            </div>
          </div>
        </div>
      </section>}

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          {children}
        </div>
      </section>
    </>
  );
}
